import React from 'react';
import Modal from 'react-modal';
import moment from 'moment';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { TeamSafetySummary } from '../../../../models';
import {
  setTeamSafetyExclusion,
  clearTeamSafetyExclusion,
} from '../../driversSlice';

import './EditInclusionModal.scss';

const modalStyles: Modal.Styles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 'none',
    borderRadius: '10px',
    width: '520px',
    maxWidth: '92vw',
    maxHeight: '84vh',
    overflow: 'hidden',
  },
  overlay: { backgroundColor: 'rgba(19,19,19,0.45)', zIndex: 1000 },
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  summary: TeamSafetySummary;
  onChanged: () => void;
}

export function EditInclusionModal({ isOpen, onClose, summary, onChanged }: Props) {
  const dispatch = useAppDispatch();
  const mutation = useAppSelector(state => state.drivers.teamSafetyMutation);
  const mutating = !!mutation?.loading;

  const [excludingId, setExcludingId] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');

  const payPeriodId = summary.payPeriod.id;

  const closeInline = () => { setExcludingId(null); setReason(''); };

  const confirmExclude = (driverId: string) => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    dispatch(setTeamSafetyExclusion({ driverId, payPeriodId, reason: trimmed }))
      .unwrap()
      .then(() => { toast.success('Driver excluded'); closeInline(); onChanged(); })
      .catch(() => toast.error('Could not exclude driver'));
  };

  const reinclude = (driverId: string) => {
    dispatch(clearTeamSafetyExclusion({ driverId, payPeriodId }))
      .unwrap()
      .then(() => { toast.success('Driver re-included'); onChanged(); })
      .catch(() => toast.error('Could not re-include driver'));
  };

  const dates = `${moment(summary.payPeriod.startDate).format('MMM D')} – ${moment(summary.payPeriod.endDate).format('MMM D, YYYY')}`;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
      contentLabel='Edit who counts toward the team safety score'
    >
      <div className='edit-inclusion-modal'>
        <div className='eim-header'>
          <div>
            <h3>Edit who counts</h3>
            <p className='eim-sub'>Pay period {dates}</p>
          </div>
          <button className='eim-close' onClick={onClose} aria-label='Close'>×</button>
        </div>

        <div className='eim-body'>
          <div className='eim-group'>
            <div className='eim-group-title'>Counted ({summary.included.length})</div>
            {summary.included.map(d => (
              <div className='eim-row' key={d.driverId}>
                <span className='eim-name'>{d.firstName} {d.lastName}</span>
                <span className='eim-score'>{d.weightedScore != null ? d.weightedScore : '—'}</span>
                {excludingId === d.driverId ? (
                  <div className='eim-inline'>
                    <input
                      className='eim-reason'
                      autoFocus
                      placeholder='Reason (required)'
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                    />
                    <button
                      className='eim-btn eim-btn--danger'
                      disabled={!reason.trim() || mutating}
                      onClick={() => confirmExclude(d.driverId)}
                    >
                      Exclude
                    </button>
                    <button className='eim-btn' disabled={mutating} onClick={closeInline}>Cancel</button>
                  </div>
                ) : (
                  <button
                    className='eim-btn'
                    disabled={mutating}
                    onClick={() => { setExcludingId(d.driverId); setReason(''); }}
                  >
                    Exclude
                  </button>
                )}
              </div>
            ))}
            {summary.included.length === 0 && (
              <div className='eim-empty'>No drivers counted this period.</div>
            )}
          </div>

          {summary.manuallyExcluded.length > 0 && (
            <div className='eim-group'>
              <div className='eim-group-title'>Manually excluded ({summary.manuallyExcluded.length})</div>
              {summary.manuallyExcluded.map(d => (
                <div className='eim-row' key={d.driverId}>
                  <span className='eim-name'>{d.firstName} {d.lastName}</span>
                  <span className='eim-reason-text' title={d.reason}>{d.reason}</span>
                  <button className='eim-btn' disabled={mutating} onClick={() => reinclude(d.driverId)}>
                    Re-include
                  </button>
                </div>
              ))}
            </div>
          )}

          {summary.autoExcluded.length > 0 && (
            <div className='eim-group'>
              <div className='eim-group-title'>Not driving this period ({summary.autoExcluded.length})</div>
              {summary.autoExcluded.map(d => (
                <div className='eim-row eim-row--muted' key={d.driverId}>
                  <span className='eim-name'>{d.firstName} {d.lastName}</span>
                  <span className='eim-auto-note'>auto — no trips</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='eim-footer'>
          <button className='eim-btn' onClick={onClose}>Done</button>
        </div>
      </div>
    </Modal>
  );
}
